// api.js - API Client for GChain

const API_BASE_URL = '';

class APIClient {
  constructor() {
    this.token = localStorage.getItem('gchain_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('gchain_token', token);
    } else {
      localStorage.removeItem('gchain_token');
    }
  }

  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers
      }
    };

    console.log('[API DEBUG] request:', options.method || 'GET', url);

    try {
      const response = await fetch(url, config);
      console.log('[API DEBUG] response status:', response.status);
      const data = await response.json();

      // Check for refreshed token (sliding window auth)
      const refreshedToken = response.headers.get('X-Refreshed-Token');
      if (refreshedToken) {
        this.setToken(refreshedToken);
      }

      if (!response.ok) {
        let httpError;
        // Handle auth errors with clear messaging
        if (response.status === 401 || response.status === 403) {
          httpError = new Error('Your session has expired (sessions last 30 days of inactivity). Please log out and log back in.');
        } else if (data.errors && Array.isArray(data.errors)) {
          // Handle validation errors array (e.g., from express-validator)
          httpError = new Error(data.errors.map(e => e.msg).join('. '));
        } else {
          httpError = new Error(data.error || 'Request failed');
        }
        // Mark errors the server answered; it has already logged any 5xx itself.
        httpError.status = response.status;
        throw httpError;
      }

      return data;
    } catch (error) {
      console.error('[API DEBUG] API request failed:', error);
      // A failure with no HTTP status never reached a route handler (network
      // down, proxy error page, non-JSON body), so the server has no record of it.
      if (!error.status) {
        this.track('error', 'api_error', `${options.method || 'GET'} ${endpoint}: ${error.message}`);
      }
      throw error;
    }
  }

  // Fire-and-forget usage/error beacon. Deliberately never throws and never
  // rejects: telemetry must not be able to break the feature it observes.
  track(kind, action, detail = null) {
    if (!this.token) return;
    try {
      fetch(`${API_BASE_URL}/api/activity`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ kind, action, detail }),
        keepalive: true
      }).catch(() => {});
    } catch (e) {
      // Swallowed on purpose; see note above.
    }
  }

  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  async register(username, password) {
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });

    this.setToken(data.token);
    return data;
  }

  async login(username, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });

    this.setToken(data.token);
    return data;
  }

  logout() {
    this.setToken(null);
  }

  isAuthenticated() {
    return !!this.token;
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  // ============================================================================
  // PROJECTS
  // ============================================================================

  async getProjects() {
    return this.request('/api/projects');
  }

  async getProject(id) {
    return this.request(`/api/projects/${id}`);
  }

  async createProject(projectData) {
    console.log('[API DEBUG] createProject called with:', projectData);
    const result = await this.request('/api/projects', {
      method: 'POST',
      body: JSON.stringify(projectData)
    });
    console.log('[API DEBUG] createProject result:', result);
    return result;
  }

  async updateProject(id, projectData) {
    console.log('[API DEBUG] updateProject called, id:', id, 'data:', projectData);
    const result = await this.request(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(projectData)
    });
    console.log('[API DEBUG] updateProject result:', result);
    return result;
  }

  async deleteProject(id) {
    return this.request(`/api/projects/${id}`, {
      method: 'DELETE'
    });
  }

  // ============================================================================
  // COMPONENTS
  // ============================================================================

  async getComponents() {
    return this.request('/api/components');
  }

  async createComponent(componentData) {
    return this.request('/api/components', {
      method: 'POST',
      body: JSON.stringify(componentData)
    });
  }

  async updateComponent(id, componentData) {
    return this.request(`/api/components/${id}`, {
      method: 'PUT',
      body: JSON.stringify(componentData)
    });
  }

  async deleteComponent(id) {
    return this.request(`/api/components/${id}`, {
      method: 'DELETE'
    });
  }

  async toggleFavorite(id, isFavorite) {
    return this.request(`/api/components/${id}/favorite`, {
      method: 'PATCH',
      body: JSON.stringify({ is_favorite: isFavorite })
    });
  }

  // ============================================================================
  // FEEDBACK
  // ============================================================================

  async submitFeedback(formData) {
    // Use FormData for file uploads - don't set Content-Type header
    const url = `${API_BASE_URL}/api/feedback`;
    const config = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    };

    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to submit feedback');
    }

    return data;
  }

  async getFeedback() {
    return this.request('/api/feedback');
  }

  async getRecentUpdates() {
    return this.request('/api/feedback/recent-updates');
  }

  async checkFeedbackUpdates() {
    return this.request('/api/feedback/check-updates');
  }

  async markFeedbackViewed() {
    return this.request('/api/feedback/mark-viewed', {
      method: 'POST'
    });
  }

  async getFeedbackFile(filename) {
    return this.request(`/api/feedback/file/${filename}`);
  }

  // ============================================================================
  // ADMIN FEEDBACK MANAGEMENT
  // ============================================================================

  async getAdminFeedback() {
    return this.request('/api/admin/feedback');
  }

  async getAdminFeedbackDetail(id) {
    return this.request(`/api/admin/feedback/${id}`);
  }

  async updateAdminFeedback(id, updates) {
    return this.request(`/api/admin/feedback/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async reorderFeedback(items) {
    return this.request('/api/admin/feedback-reorder', {
      method: 'PATCH',
      body: JSON.stringify({ items })
    });
  }

  async checkNewFeedback() {
    return this.request('/api/admin/feedback-check-new');
  }

  async markAdminFeedbackViewed() {
    return this.request('/api/admin/feedback-mark-viewed', {
      method: 'POST'
    });
  }

  // ============================================================================
  // ADMIN USAGE & ACTIVITY
  // ============================================================================

  async getAdminUsage(days) {
    return this.request(`/api/admin/usage?days=${days}`);
  }
}

const api = new APIClient();
export { api };
export default api;
